import React, { useState, useEffect } from "react";
import { displayDate } from "../../../../../../utils/date";
import InputBox from "../../../../../common/input/InputBox.js";
import Button from "../../../../../common/buttons/Button.js";
import { showErrorToast } from "../../../../../../utils/toaster.js";

const BatchDetails = ({
  batchDetails,
  formik,
  edit,
  handleSaveBatchDetails,
  selectedRowData,
}) => {
  const [reqQuantities, setReqQuantities] = useState({});

  // Function to calculate and distribute required quantities
  const calculateReqQuantities = () => {
    if (!selectedRowData?.quantity || !batchDetails?.length) return;

    const requiredQty = selectedRowData.quantity;
    const quantities = {};
    let remainingQty = requiredQty;
    let hasError = false;

    // Sort batch details by expiry date (earliest first) to use FIFO
    const sortedBatchDetails = [...batchDetails].sort(
      (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date)
    );

    for (let i = 0; i < sortedBatchDetails.length; i++) {
      const item = sortedBatchDetails[i];
      const availableQty = item.quantity;

      if (remainingQty <= 0) {
        quantities[item._id] = 0;
        continue;
      }

      if (remainingQty <= availableQty) {
        quantities[item._id] = remainingQty;
        remainingQty = 0;
      } else {
        quantities[item._id] = availableQty;
        remainingQty -= availableQty;
      }
    }

    // Check if there's insufficient stock
    if (remainingQty > 0) {
      hasError = true;
      showErrorToast(
        `Insufficient stock! Required: ${requiredQty}, Available: ${
          requiredQty - remainingQty
        }`
      );
    } else {
    }

    setReqQuantities(quantities);
  };

  // Calculate quantities when selectedRowData or batchDetails change
  useEffect(() => {
    if (!batchDetails?.length) {
      setReqQuantities({});
      return;
    }

    if (selectedRowData?.batchDetails?.length) {
      const existingQuantities = {};

      batchDetails.forEach((item) => {
        const matched = selectedRowData.batchDetails.find(
          (batch) =>
            batch?._id === item._id ||
            batch?.stock_id === item._id ||
            batch?.stockId === item._id
        );
        existingQuantities[item._id] = matched?.quantity || 0;
      });

      setReqQuantities(existingQuantities);
      return;
    }

    calculateReqQuantities();
  }, [selectedRowData, batchDetails]);

  const handleInputChange = (item, value) => {
    if (!edit) return;

    const newQuantities = {
      ...reqQuantities,
      [item._id]: parseInt(value) || 0,
    };
    setReqQuantities(newQuantities);
  };

  // Function to create batch details object for save
  const createBatchDetailsObject = () => {
    const batchDetailsArray = [];

    Object.keys(reqQuantities).forEach((batchId) => {
      const quantity = reqQuantities[batchId];
      if (quantity > 0) {
        const batchDetail = batchDetails.find((item) => item._id === batchId);
        if (batchDetail) {
          batchDetailsArray.push({
            _id: batchId,
            batch: batchDetail.batch,
            quantity: quantity,
            expiry_date: batchDetail.expiry_date,
            purchase_date: batchDetail.purchase_date,
            invoice_number: batchDetail.invoice_number,
          });
        }
      }
    });

    return batchDetailsArray;
  };

  // Handle save with batch details object
  const handleSave = () => {
    const batchDetailsObject = createBatchDetailsObject();
    const totalQuantity = Object.values(reqQuantities).reduce(
      (sum, quantity) => sum + (quantity || 0),
      0
    );
    const hasExceededAvailability = batchDetails?.some((item) => {
      const requested = reqQuantities[item._id] || 0;
      return requested > (item.quantity || 0);
    });
    if (hasExceededAvailability) {
      showErrorToast("Requested quantity exceeds available stock.");
      return;
    }
    if (totalQuantity === 0) {
      showErrorToast("Please provide a quantity before saving.");
      return;
    }
    const requiredQuantity = Number(selectedRowData?.quantity) || 0;
    if (requiredQuantity > 0 && totalQuantity !== requiredQuantity) {
      showErrorToast(
        "Unable to save. Total batch quantity must match the selected quantity."
      );
      return;
    }
    if (!batchDetailsObject.length) {
      showErrorToast(
        "Unable to save. Please review the batch quantities selected."
      );
      return;
    }

    // Call the original save function with the batch details object
    if (handleSaveBatchDetails) {
      handleSaveBatchDetails(batchDetailsObject, totalQuantity);
    }
  };

  return (
    <div className="border rounded-15 pb-5 " style={{ width: "50vw" }}>
      <div
        className={`shadow-card rounded-tl-15 rounded-tr-15 bg-secondary p-3 text-bodyBB text-darkgrey mb-2 `}
      >
        Batch Details
      </div>

      <table className=" w-screen md:w-full border-separate border-spacing-y-1 min-w-max ">
        <thead className="">
          <tr>
            <th className="border-b pb-2 text-bodyBB text-darkgrey h-[50px]">
              Batch No
            </th>
            <th className="border-b pb-2 text-bodyBB text-darkgrey">
              Exp date
            </th>
            <th className="border-b pb-2 text-bodyBB text-darkgrey">
              Available Qty
            </th>
            <th className="border-b pb-2 text-bodyBB text-darkgrey">Req Qty</th>
          </tr>
        </thead>
        <tbody>
          {batchDetails?.map((item, index) => (
            <tr className="" key={index}>
              <td className="text-center p-2 text-bodyRB text-darkgrey">
                {item.batch}
              </td>
              <td className="text-center p-2 text-bodyRB text-darkgrey">
                {item.expiry_date ? displayDate(item.expiry_date) : "-"}
              </td>
              <td className="text-center p-2 text-bodyRB text-darkgrey">
                {item.quantity || "-"}
              </td>
              <td className={`p-2 pr-5 !w-[80px]`}>
                <div className="relative flex items-center">
                  <InputBox
                    type="number"
                    disabled={!edit}
                    className={"h-[45px] !w-[80px]"}
                    value={reqQuantities[item._id] || 0}
                    onChange={(e) => handleInputChange(item, e.target.value)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-4 mt-10 pr-2">
        <Button
          type={"primary"}
          className={" h-[40px] col-start-4"}
          action={"button"}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default BatchDetails;
